import { useState } from "react";
import { Search } from "lucide-react";
import Button from "../../../../components/EcommerceActionButton/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../../components/Dialog";
import { Badge } from "../../../../components/Badge";
import {
  catalogProducts,
  type CatalogProduct,
} from "../../data/stoneflexCatalog";
import {
  calculateSheetsFromM2,
  calculateM2FromSheets,
} from "../../utils/measurementCalculations";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import { warningToast } from "../../../../services/toasts";
import styles from "./ProductSelector.module.scss";

interface ProductSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProduct: (product: QuoteProduct) => void;
  selectedProducts: string[];
}

export function ProductSelector({
  isOpen,
  onClose,
  onSelectProduct,
  selectedProducts,
}: ProductSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLine, setSelectedLine] = useState("all");
  const [quantityValues, setQuantityValues] = useState<Record<string, number>>(
    {}
  );
  const [quantityUnits, setQuantityUnits] = useState<
    Record<string, "m2" | "sheets">
  >({});

  const getStockStatus = (warehouseStock: number, freeZoneStock: number) => {
    const totalStock = warehouseStock + freeZoneStock;
    return totalStock === 0
      ? "out-of-stock"
      : totalStock < 10
      ? "low-stock"
      : "in-stock";
  };

  const handleQuantityChange = (productId: string, value: number) => {
    setQuantityValues((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleUnitChange = (productId: string, unit: "m2" | "sheets") => {
    setQuantityUnits((prev) => ({
      ...prev,
      [productId]: unit,
    }));
  };

  const handleAddProduct = (product: CatalogProduct) => {
    const quantityValue = quantityValues[product.id] || 0;
    const unit = quantityUnits[product.id] || "m2";
    const totalStock = product.warehouseStock + product.freeZoneStock;

    const calculation =
      unit === "sheets"
        ? calculateM2FromSheets(
            product.widthCm,
            product.lengthCm,
            quantityValue
          )
        : calculateSheetsFromM2(
            product.widthCm,
            product.lengthCm,
            quantityValue || 1
          );

    const quoteProduct: QuoteProduct = {
      id: product.id,
      name: product.reference,
      quantity: calculation.sheetsNeeded,
      price: product.price,
      discountPercent: 0,
      widthCm: product.widthCm,
      lengthCm: product.lengthCm,
      requestedM2: calculation.calculatedM2,
      sheetsNeeded: calculation.sheetsNeeded,
      adhesiveName: product.adhesiveName,
      adhesiveUnitsPerSheet: product.adhesiveUnitsPerSheet,
      adhesiveUnitPrice: product.adhesiveUnitPrice,
      sealantName: product.sealantName,
      sealantCoverageM2PerQuart: product.sealantCoverageM2PerQuart,
      sealantQuartPrice: product.sealantQuartPrice,
    };

    onSelectProduct(quoteProduct);

    const stockStatus = getStockStatus(
      product.warehouseStock,
      product.freeZoneStock
    );
    if (stockStatus === "out-of-stock") {
      warningToast("Producto agotado agregado. Sujeto a reposición.");
    } else if (calculation.sheetsNeeded > totalStock) {
      warningToast(
        `Cantidad solicitada (${calculation.sheetsNeeded} láminas) supera el stock disponible (${totalStock}).`
      );
    }

    setQuantityValues((prev) => {
      const newQuantities = { ...prev };
      delete newQuantities[product.id];
      return newQuantities;
    });
    setQuantityUnits((prev) => {
      const newUnits = { ...prev };
      delete newUnits[product.id];
      return newUnits;
    });
  };

  const lines = [
    "all",
    ...Array.from(new Set(catalogProducts.map((p) => p.line))),
  ];

  const filteredProducts = catalogProducts.filter((product) => {
    const matchesSearch = product.reference
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesLine = selectedLine === "all" || product.line === selectedLine;
    return matchesSearch && matchesLine;
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={styles.dialogContent}>
        <DialogHeader className={styles.dialogHeader}>
          <DialogTitle className={styles.dialogTitle}>
            Seleccionar Productos del Inventario
          </DialogTitle>
          <DialogDescription className={styles.dialogDescription}>
            Busca y selecciona los productos que deseas agregar a tu pedido.
          </DialogDescription>
        </DialogHeader>

        <div className={styles.filtersSection}>
          <div className={styles.filtersContainer}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Buscar por referencia..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>
            <select
              value={selectedLine}
              onChange={(e) => setSelectedLine(e.target.value)}
              className={styles.selectFilter}
            >
              {lines.map((line) => (
                <option key={line} value={line}>
                  {line === "all" ? "Todas las líneas" : line}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.productsList}>
          <div className={styles.productsContainer}>
            {filteredProducts.map((product) => {
              const totalStock = product.warehouseStock + product.freeZoneStock;
              const isSelected = selectedProducts.includes(product.id);
              const stockStatus = getStockStatus(
                product.warehouseStock,
                product.freeZoneStock
              );
              const isOutOfStock = stockStatus === "out-of-stock";

              return (
                <div
                  key={product.id}
                  className={`${styles.productCard} ${
                    isSelected ? styles.selected : ""
                  }`}
                >
                  <div className={styles.productInfo}>
                    <div className={styles.productHeader}>
                      <div className={styles.productDetails}>
                        <h4 className={styles.productName}>
                          {product.reference}
                        </h4>
                        <div className={styles.productMetadata}>
                          <Badge variant="outline" className="text-xs">
                            {product.line}
                          </Badge>
                          <span className={styles.productDimensions}>
                            {product.widthCm} × {product.lengthCm}
                          </span>
                          <span
                            className={`${styles.stockBadge} ${
                              stockStatus === "out-of-stock"
                                ? styles.outOfStock
                                : stockStatus === "low-stock"
                                ? styles.lowStock
                                : styles.inStock
                            }`}
                          >
                            Stock disponible: {totalStock}
                          </span>
                        </div>
                      </div>
                      <div className={styles.productPrice}>
                        <div className={styles.price}>
                          {formatNumberToPrice(product.price)}
                        </div>
                        <div className={styles.priceNote}>+ IVA</div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.productActions}>
                    {!isSelected && (
                      <div className={styles.quantitySection}>
                        <div className={styles.quantityInputWrapper}>
                          <div className={styles.quantityField}>
                            <input
                              type="number"
                              min={
                                (quantityUnits[product.id] || "m2") === "m2"
                                  ? "0.1"
                                  : "1"
                              }
                              step={
                                (quantityUnits[product.id] || "m2") === "m2"
                                  ? "0.1"
                                  : "1"
                              }
                              value={quantityValues[product.id] || ""}
                              onChange={(e) =>
                                handleQuantityChange(
                                  product.id,
                                  (quantityUnits[product.id] || "m2") === "m2"
                                    ? parseFloat(e.target.value) || 0
                                    : parseInt(e.target.value, 10) || 0
                                )
                              }
                              className={styles.quantityInput}
                            />
                            <select
                              value={quantityUnits[product.id] || "m2"}
                              onChange={(e) =>
                                handleUnitChange(
                                  product.id,
                                  e.target.value as "m2" | "sheets"
                                )
                              }
                              className={styles.quantitySelect}
                            >
                              <option value="m2">m²</option>
                              <option value="sheets">láminas</option>
                            </select>
                          </div>
                          <Button
                            color="primary"
                            shape="little-rounded"
                            onClick={() => handleAddProduct(product)}
                            disabled={
                              !quantityValues[product.id] ||
                              quantityValues[product.id] <= 0
                            }
                          >
                            Agregar
                          </Button>
                        </div>
                        {quantityValues[product.id] > 0 &&
                          (() => {
                            const calc =
                              (quantityUnits[product.id] || "m2") === "sheets"
                                ? calculateM2FromSheets(
                                    product.widthCm,
                                    product.lengthCm,
                                    quantityValues[product.id]
                                  )
                                : calculateSheetsFromM2(
                                    product.widthCm,
                                    product.lengthCm,
                                    quantityValues[product.id]
                                  );

                            return (
                              <div className={styles.calculationDetails}>
                                {calc.sheetsNeeded} láminas •{" "}
                                {calc.calculatedM2} m²
                              </div>
                            );
                          })()}
                      </div>
                    )}
                    {isSelected && <Badge variant="success">Agregado</Badge>}
                    {isOutOfStock && <Badge variant="danger">Agotado</Badge>}
                  </div>
                </div>
              );
            })}
          </div>

          {filteredProducts.length === 0 && (
            <div className={styles.emptyState}>
              <p>No se encontraron productos</p>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <Button color="outlined" shape="little-rounded" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
